/**
 * HTTP client for tenant brand kits (base /api/v2/distribution-brand-kits; routes in
 * distributionBrandKits.types.ts). Writes answer 202; poll `getBrandKitEdit` until done
 * (distributionEdits.ts `waitForBrandKitEdit`). Writes send an `Idempotency-Key`.
 * REACT_APP_DISTRIBUTIONS_API=mock answers from the in-memory mock instead.
 */
import { API_ENDPOINTS, apiService } from "../api";
import { newIdempotencyKey, writeOptions } from "./distributionIdempotency";
import { isMockDistributionsApi } from "./distributionsApi";
import * as mock from "./distributionsApi.mock";
import type {
  BrandKitAcceptedApi,
  BrandKitApi,
  BrandKitContentApi,
  BrandKitListApi,
  LogoUploadedApi,
} from "./distributionBrandKits.types";
import type { EditStatusApi } from "./distributions.types";

const E = API_ENDPOINTS.distributionBrandKits;

export const listBrandKits = (): Promise<BrandKitListApi> =>
  isMockDistributionsApi() ? mock.mockListBrandKits() : apiService.get<BrandKitListApi>(E.list);

export const getBrandKit = (kitId: string): Promise<BrandKitApi> =>
  isMockDistributionsApi() ? mock.mockGetBrandKit(kitId) : apiService.get<BrandKitApi>(E.detail(kitId));

export const createBrandKit = (body: BrandKitContentApi, idempotencyKey = newIdempotencyKey()): Promise<BrandKitAcceptedApi> =>
  isMockDistributionsApi() ? mock.mockCreateBrandKit(body) : apiService.post<BrandKitAcceptedApi>(E.list, body, writeOptions(idempotencyKey));

/** Replaces the kit's content wholesale. */
export const updateBrandKit = (kitId: string, body: BrandKitContentApi, idempotencyKey = newIdempotencyKey()): Promise<BrandKitAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockUpdateBrandKit(kitId, body)
    : apiService.put<BrandKitAcceptedApi>(E.detail(kitId), body, undefined, writeOptions(idempotencyKey));

/** Archives the kit. Distributions that name it render with their own fields until changed. */
export const deleteBrandKit = (kitId: string, idempotencyKey = newIdempotencyKey()): Promise<BrandKitAcceptedApi> =>
  isMockDistributionsApi() ? mock.mockDeleteBrandKit(kitId) : apiService.delete<BrandKitAcceptedApi>(E.detail(kitId), writeOptions(idempotencyKey));

/** Makes it the kit new distributions start with (one per tenant). */
export const setDefaultBrandKit = (kitId: string, idempotencyKey = newIdempotencyKey()): Promise<BrandKitAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockSetDefaultBrandKit(kitId)
    : apiService.post<BrandKitAcceptedApi>(E.setDefault(kitId), {}, writeOptions(idempotencyKey));

export const getBrandKitEdit = (requestId: string): Promise<EditStatusApi> =>
  isMockDistributionsApi() ? mock.mockGetBrandKitEdit(requestId) : apiService.get<EditStatusApi>(E.edit(requestId));

/**
 * Uploads one logo through api-service (the buckets take no browser PUTs). `file` is a PNG or a
 * JPEG of at most 2 MB; an SVG is converted to PNG first (utils/imageFiles.ts). The answer's key
 * and id go into the kit's `logos` on the next save.
 */
export const uploadBrandLogo = (file: Blob, fileName: string): Promise<LogoUploadedApi> => {
  if (isMockDistributionsApi()) return mock.mockUploadBrandLogo(file, fileName);
  const form = new FormData();
  form.append("file", file, fileName);
  return apiService.postFormData<LogoUploadedApi>(E.logos, form);
};
